import { AxiosError } from "axios";

function isXmlContent(data: string): boolean {
    return data.trimStart().startsWith("<");
}

export class NotificationOptions {
    static readonly presetBase = {
        message: "",
        title: "",
        timeout: 10000,
        icon: "now-ui-icons ui-1_bell-53",
        horizontalAlign: "center",
        verticalAlign: "top",
        type: ""
    };

    static error(message: any, title?: string): any {
        return this.notificationOptionsPreset(message, NotificationOptions.NotificationTypes.danger, title);
    }

    static success(message: any, title?: string): any {
        return this.notificationOptionsPreset(message, NotificationOptions.NotificationTypes.success, title);
    }

    static warning(message: any, title?: string): any {
        return this.notificationOptionsPreset(message, NotificationOptions.NotificationTypes.warning, title);
    }

    static notificationOptionsPreset(message: any, typeMessage: NotificationOptions.NotificationTypes, title?: string): any {
        const typeNames: Record<number, string> = { 0: "success", 1: "danger", 2: "warning", 3: "info" };
        function isAxiosError(item: any): item is AxiosError {
            return (item as AxiosError).isAxiosError !== undefined;
        }

        let baseMessage = message;
        if (isAxiosError(message)) {
            if (message.response?.data) {
                // 418 errors should be handled with showLockedMessage() instead of generic error notifications
                if (message.response.status === 418) {
                    console.warn('HTTP 418 (Locked Item) error passed to NotificationOptions.error(). Consider using showLockedMessage() from LockHandler mixin for better UX.', message.response.data);
                }
                if (message.response.status === 409) typeMessage = NotificationOptions.NotificationTypes.warning;
                baseMessage = message.response.data;
            }
        }

        if (baseMessage?.message?.length) {
            baseMessage = baseMessage.message;
        } else {
            if (message.errors) baseMessage = message.errors;
            if (message.message) baseMessage = message.message;
        }

        if (message.response?.status === 429) {
            const retryAfter = message.response?.headers?.['retry-after'];
            if (retryAfter) {
                baseMessage = `Too many requests. Please wait ${retryAfter} seconds and try again.`;
            } else {
                baseMessage = `Too many requests. Please wait a moment and try again.`;
            }
            typeMessage = NotificationOptions.NotificationTypes.warning;
        } else if (message.response?.status === 403)
            baseMessage = `You're not authorized to perform this action. ${message.response.data}`;
        else if (typeof message.response?.data === "string" && message.response.data.length) {
            baseMessage = isXmlContent(message.response.data)
                ? `Request failed (${message?.response?.status})`
                : `${message.response.data} (${message?.response?.status})`;
        }

        return {
            ...this.presetBase,
            message: baseMessage,
            title: title ?? "",
            type: typeNames[typeMessage],
        };
    }

    static successSaveNotificationPreset(entityName: string): any {
        return {
            ...this.presetBase,
            message: `${entityName} Successfully Saved`,
            type: "success",
        };
    }

    static successDeletedNotificationPreset(entityName: string): any {
        return {
            ...this.presetBase,
            message: `${entityName} Successfully Deleted`,
            type: "success",
        };
    }

    static errorSaveNotificationPreset(entityName: string, error?: AxiosError): any {
        let message = `${entityName} Failed to Save`;

        if (error?.response?.status === 403)
            message += `. You are not authorized to perform this save action. ${error.response.data}`;
        else if (typeof error?.response?.data === "string" && error?.response.data.length) {
            message += isXmlContent(error.response.data)
                ? ` (${error?.response?.status})`
                : `. ${error.response.data} (${error?.response?.status})`;
        }

        return {
            ...this.presetBase,
            message,
            type: "danger",
        };
    }
}

export namespace NotificationOptions {

    export enum NotificationTypes {
        success,
        danger,
        warning,
        info
    }
}
